import React, { FunctionComponent, memo, useEffect, useLayoutEffect, useRef } from 'react';
import { useContentLoader } from '../../hooks';
import { useAllSceneAutoplayVideoIds, useGet360VideoIdBySceneId } from '../../hooks/general';
import { Jute } from '../../index';
import { incompatibleUI, isSupportedBrowser } from '../../utils';
import { IDataActions, IEntityActionTypes, IMode, useActionStateStore, useDataStore } from '../../zustand';
import Root from '../Root';
import { IContentDoc, IRoot, ISceneComp, ITrackingTypes } from '../r3f-components/component-data-structure';
import { useGetActiveTrackingType } from '../hoc/hocCustomHooks';
import { BufferGeometry, Mesh } from 'three';
import { acceleratedRaycast, computeBoundsTree } from 'three-mesh-bvh';

BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
Mesh.prototype.raycast = acceleratedRaycast;

interface IParentProps {
	contentDoc: IContentDoc;
}
 
const App: FunctionComponent<IParentProps> = ({ contentDoc }) => {
	const dataDispatch = useDataStore(state => state.dispatch);
	const entityActionDispatch = useActionStateStore(state => state.dispatch);
	const componentsById = useDataStore(state => state.componentsById);
	const rootId = useDataStore(state => state.rootId);
	const sceneIds = (componentsById?.[rootId || ''] as IRoot)?.children ?? [];
	const menuSceneIds = sceneIds.filter((sceneId) => (componentsById?.[sceneId] as ISceneComp)?.trackingType === ITrackingTypes.noTrackingScreen);
	const firstSceneId = (componentsById?.[rootId ?? ''] as IRoot)?.children?.[0];
	const _visibleFirstSceneAutoplayVideos = useAllSceneAutoplayVideoIds(firstSceneId, true);
	const visibleFirstSceneAutoplayVideos = [..._visibleFirstSceneAutoplayVideos];
	const firstSceneIsMenuScene = useGetActiveTrackingType();
	const juteOnlaunchAutoplayRef = useRef(false);

	const firstSceneBackgroundVideo = useGet360VideoIdBySceneId(firstSceneId);

	if (firstSceneBackgroundVideo) visibleFirstSceneAutoplayVideos.push(firstSceneBackgroundVideo)

	const firstSceneVideo360Id = useGet360VideoIdBySceneId(firstSceneId);

	useLayoutEffect(() => {
		if (!componentsById) return;
		for (let i = 0; i < menuSceneIds.length; i++) {
			const menuSceneId = menuSceneIds[i];
			entityActionDispatch?.({
				type: IEntityActionTypes.SETUP_ALL_SCENE_AUDIO_BY_SCENE_ID,
				payload: { id: menuSceneId, componentsById },
			});
		}
	}, [menuSceneIds, componentsById, entityActionDispatch]);

	//TODO: needs checking if still fullfills all user interaction requirements for autoplay
	useLayoutEffect(() => {
		// create muted autoplay video nodes
		entityActionDispatch?.({
			type: IEntityActionTypes.CREATE_AUTOPLAY_VIDEO_NODES,
		});

		dataDispatch({
			type: IDataActions.SET_MODE,
			mode: Jute.isIframe ? IMode.nonar : IMode.ar,
		});

		Jute.onLaunch(() => {
			// Ensure that Chrome runs sound & video on user interaction
			if (Jute.isIframe) return;
			entityActionDispatch?.({
				type: IEntityActionTypes.UNMUTE_AUTOPLAY_VIDEOS,
			});
			entityActionDispatch?.({
				type: IEntityActionTypes.UNMUTE_BACKGROUND_AUDIO,
			});
		});

		Jute.onReady(() => {
			if (Jute.isIframe) return;
			entityActionDispatch?.({
				type: IEntityActionTypes.PLAY_BACKGROUND_AUDIO,
			});
		});
	}, []);

	useEffect(() => {
		if (visibleFirstSceneAutoplayVideos.length > 0 && !juteOnlaunchAutoplayRef.current && firstSceneIsMenuScene) {
			if (firstSceneVideo360Id) visibleFirstSceneAutoplayVideos.push(firstSceneVideo360Id)
			Jute.onLaunch(() => {
				entityActionDispatch?.({
					type: IEntityActionTypes.PLAY_VIDEOS_BY_ID,
					payload: { ids: visibleFirstSceneAutoplayVideos },
				});
			});
			juteOnlaunchAutoplayRef.current = true;
		}
	}, [visibleFirstSceneAutoplayVideos.length, firstSceneVideo360Id]);

	useLayoutEffect(() => {
		if (!isSupportedBrowser() && Jute.environment !== 'test') {
			Jute.unsupported();
			incompatibleUI();
		}
	}, []);

	// Load and setup content state
	useContentLoader(contentDoc);

	return <Root />;
};

export default memo(App);
